import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ApiContext } from '@/lib/api'
import type { QueryRoute } from '@/lib/api/queryApi'
import { env } from '@/lib/env'

export type ThemePreference = 'system' | 'light' | 'dark'
export type TableDensity = 'comfortable' | 'compact'

export interface SettingsState {
  baseUrl: string
  route: QueryRoute
  /** Optional selected database. Required by the backend when multiple DBs are connected. */
  database: string
  /** Host-issued JWT. Only ever sent on the production route. */
  bearerToken: string
  requestTimeoutMs: number
  theme: ThemePreference
  density: TableDensity
  /** Drives every `toLocaleString` call in the formatting layer. */
  locale: string
  /** Keep the thread pinned to the newest exchange while one is running. */
  autoScroll: boolean
  /** Persist queries to the History view. */
  rememberHistory: boolean

  setBaseUrl: (value: string) => void
  setRoute: (value: QueryRoute) => void
  setDatabase: (value: string) => void
  setBearerToken: (value: string) => void
  setRequestTimeoutMs: (value: number) => void
  setTheme: (value: ThemePreference) => void
  setDensity: (value: TableDensity) => void
  setLocale: (value: string) => void
  setAutoScroll: (value: boolean) => void
  setRememberHistory: (value: boolean) => void
  reset: () => void
}

const DEFAULTS = {
  baseUrl: env.apiBaseUrl,
  route: env.defaultRoute,
  database: '',
  bearerToken: '',
  requestTimeoutMs: 120_000,
  theme: 'system' as ThemePreference,
  density: 'comfortable' as TableDensity,
  locale: 'en-IN',
  autoScroll: true,
  rememberHistory: true,
}

export const TIMEOUT_BOUNDS = { min: 5_000, max: 600_000 }

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...DEFAULTS,

      setBaseUrl: (value) => set({ baseUrl: value }),
      setRoute: (value) => set({ route: value }),
      setDatabase: (value) => set({ database: value.trim() }),
      setBearerToken: (value) => set({ bearerToken: value.trim() }),
      setRequestTimeoutMs: (value) =>
        set({
          requestTimeoutMs: Math.min(
            TIMEOUT_BOUNDS.max,
            Math.max(TIMEOUT_BOUNDS.min, Math.round(value)),
          ),
        }),
      setTheme: (value) => set({ theme: value }),
      setDensity: (value) => set({ density: value }),
      setLocale: (value) => set({ locale: value }),
      setAutoScroll: (value) => set({ autoScroll: value }),
      setRememberHistory: (value) => set({ rememberHistory: value }),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: 'qap.settings.v1',
      version: 1,
      partialize: (state) => ({
        baseUrl: state.baseUrl,
        route: state.route,
        database: state.database,
        bearerToken: state.bearerToken,
        requestTimeoutMs: state.requestTimeoutMs,
        theme: state.theme,
        density: state.density,
        locale: state.locale,
        autoScroll: state.autoScroll,
        rememberHistory: state.rememberHistory,
      }),
    },
  ),
)

/**
 * The API layer takes an explicit context; this is the only bridge from the
 * settings store to it. A token is attached solely on the production route so a
 * stale token can never leak onto the unauthenticated test route.
 */
export function selectApiContext(state: SettingsState): ApiContext {
  return {
    baseUrl: state.baseUrl,
    timeoutMs: state.requestTimeoutMs,
    ...(state.route === 'production' && state.bearerToken
      ? { bearerToken: state.bearerToken }
      : {}),
  }
}
